import React, { useEffect, useRef, useState } from 'react';

import { Button } from '../../element/Button/Button';
import { CondensedLabel } from '../CondensedLabel/CondensedLabel';
import { IconButton } from '../../element/Button/IconButton';
import { Layout } from '../../layout/Layout/Layout';
import { LayoutItem } from '../../layout/Layout/LayoutItem';
import { Modal } from '../../element/Modal/Modal';
import { ModalFooter } from '../../element/Modal/ModalFooter';
import { ModalHeader } from '../../element/Modal/ModalHeader';
import { ModalSection } from '../../element/Modal/ModalSection';
import { RawTextarea } from '../../form/Textarea/RawTextarea';
import { ScrollRegion } from '../../layout/ScrollRegion/ScrollRegion';

import { FlowchartUtil } from './flowchart-util';

const MAX_HEIGHT = '500px';
const EXPANDED_MAX_HEIGHT = '55vh';
const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 3;

export type FlowchartProps = {
  code: string;
  name: string;
};

export const Flowchart = ({ code, name }: FlowchartProps) => {
  const [drawn, setDrawn] = useState<DocumentFragment | null>(null);
  const [failed, setFailed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const chartRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;

    setFailed(false);

    void FlowchartUtil.toFragment({ code, id: `flowchart-${name}` })
      .then(fragment => {
        if (live) setDrawn(fragment);
      })
      .catch(() => {
        if (live) setFailed(true);
      });

    return () => {
      live = false;
    };
  }, [code, name]);

  /* Appending a fragment empties it, so a copy is what survives this running twice. */
  useEffect(() => {
    if (drawn) chartRef.current?.replaceChildren(drawn.cloneNode(true));
  }, [drawn]);

  /*
    Sizing the svg against the width it was drawn at zooms the picture inside its viewBox, and
    being a real size rather than a transform is what lets the region scroll to match.
  */
  useEffect(() => {
    const node = expandedRef.current;

    if (!drawn || !isExpanded || !node) return;

    node.replaceChildren(drawn.cloneNode(true));

    const svg = node.querySelector('svg');

    if (!svg) return;

    svg.style.width = `${Number(svg.getAttribute('width')) * zoom}px`;
    svg.style.height = `${Number(svg.getAttribute('height')) * zoom}px`;
  }, [drawn, isExpanded, zoom]);

  /*
    Model output, so a diagram that will not draw is expected: fall back to what it says.
    `RawTextarea` rather than `ReadonlyTextarea`, which draws this component and cannot import it.
  */
  if (failed) {
    return (
      <RawTextarea
        formatting="modern"
        name={`FLOWCHART_SOURCE_${name}`}
        value={['```', code, '```'].join('\n')}
        readonly
        stretched
      />
    );
  }

  return (
    <>
      <Layout position="right">
        <Button
          icon="open"
          name={`EXPAND_FLOWCHART_${name}`}
          onClick={() => {
            setZoom(1);
            setIsExpanded(true);
          }}
          pattern="secondary"
          variation="minimal"
        >
          Expand diagram
        </Button>
      </Layout>
      <ScrollRegion
        axis="both"
        maxHeight={MAX_HEIGHT}
        name={`FLOWCHART_SCROLL_${name}`}
      >
        <Layout position="center">
          <div data-test={name} ref={chartRef} />
        </Layout>
      </ScrollRegion>
      {isExpanded ? (
        <Modal name={`FLOWCHART_MODAL_${name}`} size="large">
          <ModalHeader
            name={`FLOWCHART_MODAL_HEADER_${name}`}
            onClose={() => {
              setIsExpanded(false);
            }}
          >
            Flowchart
          </ModalHeader>
          <ModalSection name={`FLOWCHART_MODAL_BODY_${name}`}>
            <Layout position="right middle" direction="horizontal">
              <LayoutItem>
                <IconButton
                  disabled={zoom <= ZOOM_MIN}
                  icon="minus"
                  name={`FLOWCHART_ZOOM_OUT_${name}`}
                  onClick={() => {
                    setZoom(current => Math.max(ZOOM_MIN, current - ZOOM_STEP));
                  }}
                  pattern="secondary"
                  variation="minimal"
                />
              </LayoutItem>
              <LayoutItem horizontalGutter="8px">
                <CondensedLabel name={`FLOWCHART_ZOOM_LEVEL_${name}`}>
                  {Math.round(zoom * 100)}%
                </CondensedLabel>
              </LayoutItem>
              <LayoutItem rightGutter="8px">
                <IconButton
                  disabled={zoom >= ZOOM_MAX}
                  icon="plus"
                  name={`FLOWCHART_ZOOM_IN_${name}`}
                  onClick={() => {
                    setZoom(current => Math.min(ZOOM_MAX, current + ZOOM_STEP));
                  }}
                  pattern="secondary"
                  variation="minimal"
                />
              </LayoutItem>
              <LayoutItem>
                <IconButton
                  disabled={zoom === 1}
                  icon="reset"
                  name={`FLOWCHART_ZOOM_RESET_${name}`}
                  onClick={() => {
                    setZoom(1);
                  }}
                  pattern="secondary"
                  variation="minimal"
                />
              </LayoutItem>
            </Layout>
            <ScrollRegion
              axis="both"
              maxHeight={EXPANDED_MAX_HEIGHT}
              name={`FLOWCHART_MODAL_SCROLL_${name}`}
            >
              <Layout position="center">
                <div
                  data-test={`EXPANDED_FLOWCHART_${name}`}
                  ref={expandedRef}
                />
              </Layout>
            </ScrollRegion>
          </ModalSection>
          <ModalFooter name={`FLOWCHART_MODAL_ACTIONS_${name}`}>
            <Button
              name={`FLOWCHART_MODAL_CLOSE_${name}`}
              onClick={() => {
                setIsExpanded(false);
              }}
              variation="outline"
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>
      ) : null}
    </>
  );
};
